import { useEffect, useRef, useState } from "react";
import { RenderTree, type RenderNode } from "../ui/Render";
import { Chip } from "../ui/Chip";

type Message =
  | { role: "user"; content: string }
  | { role: "assistant"; content: string }
  | { role: "tool"; name: string; status: "ok" | "error" | "unknown_tool"; evidence?: unknown };

export function Copilot({ seed, onSeedConsumed }: { seed?: string; onSeedConsumed?: () => void } = {}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [tree, setTree] = useState<ReadonlyArray<RenderNode>>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [paymentNotice, setPaymentNotice] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, tree]);

  // When a Try-this button seeds the input from outside, populate it but
  // do NOT auto-send — the user reviews and presses Ask.
  useEffect(() => {
    if (seed) {
      setInput(seed);
      onSeedConsumed?.();
    }
  }, [seed, onSeedConsumed]);

  async function ask(prompt: string) {
    if (!prompt.trim()) return;
    setMessages((m) => [...m, { role: "user", content: prompt }]);
    setInput("");
    setBusy(true);
    setPaymentNotice(null);

    const history = messages
      .filter((m): m is Extract<Message, { content: string }> => m.role !== "tool")
      .map((m) => ({ role: m.role, content: m.content }));

    const r = await fetch("/api/aiko/stream", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        messages: [...history, { role: "user", content: prompt }],
      }),
    });

    if (!r.ok || !r.body) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Something interrupted that. Please try again." },
      ]);
      setBusy(false);
      return;
    }

    const reader = r.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    let assistantText = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const events = buf.split("\n\n");
      buf = events.pop() ?? "";
      for (const block of events) {
        const eMatch = block.match(/^event: (.+)$/m);
        const dMatch = block.match(/^data: (.+)$/m);
        if (!eMatch || !dMatch) continue;
        const event = eMatch[1];
        let data: { text?: string; tree?: RenderNode[]; name?: string; status?: "ok" | "error" | "unknown_tool"; evidence?: unknown; message?: string; upstream?: string; body?: string };
        try { data = JSON.parse(dMatch[1]); } catch { continue; }

        if (event === "token") {
          assistantText += data.text ?? "";
          setMessages((m) => {
            const out = [...m];
            const last = out[out.length - 1];
            if (last?.role === "assistant") {
              out[out.length - 1] = { role: "assistant", content: assistantText };
            } else {
              out.push({ role: "assistant", content: assistantText });
            }
            return out;
          });
        } else if (event === "tool") {
          setMessages((m) => [
            ...m,
            { role: "tool", name: data.name ?? "?", status: (data.status ?? "ok") as "ok" | "error" | "unknown_tool", evidence: data.evidence },
          ]);
        } else if (event === "render") {
          setTree(data.tree ?? []);
        } else if (event === "payment") {
          setPaymentNotice(
            "Aiko needs credits topped up before she can finish that. Open the billing surface and add agent in credits.",
          );
        } else if (event === "error") {
          setMessages((m) => [
            ...m,
            { role: "assistant", content: data.message ?? "Something interrupted that." },
          ]);
        }
      }
    }
    setBusy(false);
  }

  return (
    <div className="h-full flex flex-col">
      <header className="px-4 py-3 border-b border-line">
        <div className="font-display text-lg">Aiko</div>
        <div className="text-xs text-ink-soft">
          She drafts and runs. She pauses before she sends.
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && tree.length === 0 && (
          <div className="text-sm text-ink-soft space-y-2">
            <p>Try one of these:</p>
            <ul className="space-y-1">
              {[
                "Show me this week's spend",
                "What's pending approval?",
                "Top suppliers by spend last month",
                "Outstanding invoices",
              ].map((q) => (
                <li key={q}>
                  <button
                    className="text-accent-ink hover:underline text-left"
                    onClick={() => void ask(q)}
                  >
                    {q}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {messages.map((m, i) => {
          if (m.role === "tool") {
            return (
              <div key={i} className="flex">
                <Chip tone={m.status === "ok" ? "good" : "bad"} size="sm">
                  {m.name} · {m.status}
                </Chip>
              </div>
            );
          }
          return (
            <div
              key={i}
              className={`text-sm whitespace-pre-wrap ${
                m.role === "user" ? "text-ink" : "text-ink-soft"
              }`}
            >
              <span className="font-medium mr-2">
                {m.role === "user" ? "You" : "Aiko"}
              </span>
              {m.content}
            </div>
          );
        })}

        {tree.length > 0 && (
          <div className="pt-3 border-t border-line/60">
            <RenderTree tree={tree} />
          </div>
        )}

        {paymentNotice && (
          <div className="rounded-md border border-warn/40 bg-warn/10 text-warn px-3 py-2 text-sm">
            {paymentNotice}
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void ask(input);
        }}
        className="border-t border-line p-3 flex gap-2"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={busy}
          className="flex-1 rounded-md border border-line bg-canvas px-3 py-2 text-sm"
          placeholder={busy ? "Aiko is working..." : "Ask Aiko..."}
        />
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-accent text-canvas px-3 py-2 text-sm font-medium disabled:opacity-60"
        >
          Ask
        </button>
      </form>
    </div>
  );
}
